<div class="p-4 md:p-6">
  <div class="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
    <div>
      <h1 class="text-xl font-bold text-gray-900">Marketing Reports</h1>
      <p class="text-sm text-gray-500">Attribution, conversion, and CAPI delivery insights for WhatsApp ad traffic</p>
    </div>
    <div class="flex flex-wrap items-end gap-3 bg-white rounded-lg border border-gray-200 p-3">
      <div>
        <label class="block text-xs font-semibold text-gray-500 uppercase mb-1">From</label>
        <app-date-input [(ngModel)]="fromDate"></app-date-input>
      </div>
      <div>
        <label class="block text-xs font-semibold text-gray-500 uppercase mb-1">To</label>
        <app-date-input [(ngModel)]="toDate"></app-date-input>
      </div>
      <button (click)="applyFilters()"
        class="bg-green-600 hover:bg-green-700 text-white text-sm font-semibold px-4 py-2 rounded-md">
        <i class="fas fa-search mr-1"></i> Apply
      </button>
    </div>
  </div>

  <!-- Error -->
  <div *ngIf="errorMessage" class="bg-red-50 text-red-700 border border-red-200 rounded-md px-4 py-3 mb-4 text-sm">
    <i class="fas fa-exclamation-circle mr-1"></i> {{ errorMessage }}
  </div>

  <!-- Loading -->
  <div *ngIf="loading" class="text-center text-gray-500 py-10">
    <i class="fas fa-spinner fa-spin mr-1"></i> Loading reports...
  </div>

  <div *ngIf="!loading && !errorMessage">
    <!-- KPI tiles -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      <div class="bg-white rounded-lg border border-gray-200 p-4">
        <div class="text-xs font-semibold text-gray-500 uppercase mb-1">CAPI Sent</div>
        <div class="text-2xl font-bold text-green-600">{{ capiHealth?.sent ?? 0 }}</div>
      </div>
      <div class="bg-white rounded-lg border border-gray-200 p-4">
        <div class="text-xs font-semibold text-gray-500 uppercase mb-1">CAPI Pending / Failed</div>
        <div class="text-2xl font-bold text-amber-600">{{ capiHealth?.pendingOrFailed ?? 0 }}</div>
      </div>
      <div class="bg-white rounded-lg border border-gray-200 p-4">
        <div class="text-xs font-semibold text-gray-500 uppercase mb-1">Avg Days to Convert (Direct)</div>
        <div class="text-2xl font-bold text-gray-900">{{ directTimeToConvert?.averageDaysToConvert ?? '—' }}</div>
        <div class="text-xs text-gray-400" *ngIf="directTimeToConvert">n={{ directTimeToConvert.sampleSize }}</div>
      </div>
      <div class="bg-white rounded-lg border border-gray-200 p-4">
        <div class="text-xs font-semibold text-gray-500 uppercase mb-1">Avg Days to Convert (Ad)</div>
        <div class="text-2xl font-bold text-gray-900">{{ adTimeToConvert?.averageDaysToConvert ?? '—' }}</div>
        <div class="text-xs text-gray-400" *ngIf="adTimeToConvert">n={{ adTimeToConvert.sampleSize }}</div>
      </div>
    </div>

    <!-- Conversion rate by source -->
    <div class="mb-6">
      <h2 class="text-sm font-bold text-gray-700 uppercase mb-2">Conversion Rate by Month</h2>
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div class="bg-white rounded-lg border border-gray-200 p-4">
          <app-chart-wrapper [data]="conversionRateChart.direct" title="Direct — Conversion %" type="line"
            [colors]="['#3b82f6']"></app-chart-wrapper>
        </div>
        <div class="bg-white rounded-lg border border-gray-200 p-4">
          <app-chart-wrapper [data]="conversionRateChart.ad" title="Ad — Conversion %" type="line"
            [colors]="['#10b981']"></app-chart-wrapper>
        </div>
      </div>
    </div>

    <!-- Enquiry trend -->
    <div class="mb-6">
      <h2 class="text-sm font-bold text-gray-700 uppercase mb-2">Daily Enquiry Volume</h2>
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div class="bg-white rounded-lg border border-gray-200 p-4">
          <app-chart-wrapper [data]="enquiryTrendChart.direct" title="Direct — Enquiries" type="bar"
            [colors]="['#3b82f6']"></app-chart-wrapper>
        </div>
        <div class="bg-white rounded-lg border border-gray-200 p-4">
          <app-chart-wrapper [data]="enquiryTrendChart.ad" title="Ad — Enquiries" type="bar"
            [colors]="['#10b981']"></app-chart-wrapper>
        </div>
      </div>
    </div>

    <!-- Unconverted leads -->
    <div>
      <h2 class="text-sm font-bold text-gray-700 uppercase mb-2">Unconverted Leads</h2>
      <div class="bg-white rounded-lg border border-gray-200 p-4 mb-3">
        <div class="flex flex-wrap items-end gap-4">
          <div class="w-40">
            <label class="block text-xs font-semibold text-gray-500 uppercase mb-1">Source</label>
            <app-select-input [options]="sourceOptions" [(ngModel)]="unconvertedSource" (ngModelChange)="onUnconvertedSourceChange()"></app-select-input>
          </div>
          <p class="text-xs text-gray-400">Uses the date range selected above</p>
        </div>
      </div>

      <div class="bg-white rounded-lg border border-gray-200 overflow-x-auto">
        <div *ngIf="unconvertedLoading" class="text-center text-gray-500 py-6">
          <i class="fas fa-spinner fa-spin mr-1"></i> Loading leads...
        </div>
        <table *ngIf="!unconvertedLoading" class="min-w-full text-sm">
          <thead class="bg-gray-50 border-b border-gray-200">
            <tr>
              <th class="px-4 py-3 text-left font-semibold text-gray-600">Name</th>
              <th class="px-4 py-3 text-left font-semibold text-gray-600">Phone Number</th>
              <th class="px-4 py-3 text-left font-semibold text-gray-600">Source</th>
              <th class="px-4 py-3 text-left font-semibold text-gray-600">First Seen</th>
              <th class="px-4 py-3 text-right font-semibold text-gray-600">Days Since</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let lead of unconvertedLeads" class="border-b border-gray-100 hover:bg-gray-50">
              <td class="px-4 py-3">{{ lead.name || 'N/A' }}</td>
              <td class="px-4 py-3">{{ lead.phoneNumber }}</td>
              <td class="px-4 py-3">{{ lead.source }}</td>
              <td class="px-4 py-3">{{ lead.firstSeenAt | date:'mediumDate' }}</td>
              <td class="px-4 py-3 text-right">{{ lead.daysSinceFirstSeen }}</td>
            </tr>
            <tr *ngIf="unconvertedLeads.length === 0">
              <td colspan="5" class="px-4 py-10 text-center text-gray-400">No unconverted leads for the selected filters.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <app-pagination *ngIf="!unconvertedLoading && unconvertedLeads.length > 0"
        [page]="unconvertedCurrentPage" [totalPages]="unconvertedTotalPages" [pageSize]="unconvertedPageSize"
        (pageChange)="onUnconvertedPageChange($event)" (pageSizeChange)="onUnconvertedPageSizeChange($event)">
      </app-pagination>
    </div>
  </div>
</div>
